import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Navbar } from "@/components/Navbar";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, ArrowRight, PenLine } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");

  useEffect(() => {
    axios
      .get(`${API}/blog`)
      .then((res) => setPosts(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const categories = useMemo(
    () => Array.from(new Set(posts.map((p) => p.category).filter(Boolean))),
    [posts]
  );

  const filtered = useMemo(
    () =>
      posts.filter(
        (p) =>
          (!category || p.category === category) &&
          (!q ||
            p.title.toLowerCase().includes(q.toLowerCase()) ||
            (p.excerpt || "").toLowerCase().includes(q.toLowerCase()))
      ),
    [posts, q, category]
  );

  return (
    <div className="min-h-screen bg-white dark:bg-[#0f1117]" data-testid="blog-page">
      <Seo
        title="Blog — VCE study tips, text guides & exam technique"
        description="Expert insights, study tips and text breakdowns for VCE English, Maths Methods and the sciences — written by Melbourne's high-achieving tutors."
        path="/blog"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Blog",
          name: "Tutors Melbourne Blog",
          description: "VCE study tips, text guides and exam technique from Tutors Melbourne.",
        }}
      />
      <Navbar />

      <section className="hero-gradient dark:bg-transparent pt-28 pb-12 px-6">
        <div className="max-w-3xl mx-auto text-center animate-fade-in-up">
          <Badge className="bg-brand-purple-soft text-brand-purple mb-4 font-body font-semibold px-4 py-1.5 rounded-full">
            <PenLine className="inline h-3.5 w-3.5 mr-1.5 -mt-0.5" /> Blog
          </Badge>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 dark:text-white tracking-tight mb-5">
            Study smarter, <span className="text-brand-purple dark:text-brand-purple-light">score higher</span>
          </h1>
          <p className="text-lg text-slate-600 dark:text-slate-400 font-body leading-relaxed">
            Text breakdowns, essay structures, exam technique and the study habits that actually move study scores —
            written by tutors who've done it themselves.
          </p>
        </div>
      </section>

      <section className="py-10 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col sm:flex-row gap-3 mb-8">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search posts…"
                className="pl-11 rounded-full h-12"
                data-testid="blog-search"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant={!category ? "default" : "outline"} onClick={() => setCategory("")}
                className={`rounded-full font-body text-xs h-12 px-5 ${!category ? "bg-brand-purple hover:bg-brand-purple-hover text-white" : ""}`}
                data-testid="blog-filter-all">All</Button>
              {categories.map((c) => (
                <Button key={c} size="sm" variant={category === c ? "default" : "outline"} onClick={() => setCategory(c)}
                  className={`rounded-full font-body text-xs h-12 px-5 ${category === c ? "bg-brand-purple hover:bg-brand-purple-hover text-white" : ""}`}
                  data-testid={`blog-filter-${c.replace(/\s+/g, "-").toLowerCase()}`}>{c}</Button>
              ))}
            </div>
          </div>

          {loading ? (
            <p className="text-center text-slate-500 font-body py-16">Loading posts…</p>
          ) : filtered.length === 0 ? (
            <div className="text-center py-20 bg-slate-50 dark:bg-[#1a1b23] rounded-2xl" data-testid="blog-empty-state">
              <PenLine className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500 font-body">No posts published yet — new articles are on the way.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((p, i) => (
                <Reveal key={p.id} delay={(i % 3) * 100}>
                  <Link
                    to={`/blog/${p.slug}`}
                    className="group block h-full bg-white dark:bg-[#1a1b23] rounded-2xl border border-slate-100 dark:border-slate-800 overflow-hidden card-hover"
                    data-testid={`blog-card-${p.slug}`}
                  >
                    <div className="aspect-[16/9] bg-gradient-to-br from-brand-purple/15 to-brand-gold/15 overflow-hidden">
                      {p.cover_image ? (
                        <img src={p.cover_image} alt={p.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <PenLine className="h-8 w-8 text-brand-purple/40" />
                        </div>
                      )}
                    </div>
                    <div className="p-6">
                      {p.category && (
                        <p className="text-[11px] uppercase tracking-wider text-brand-gold font-body font-semibold mb-2">{p.category}</p>
                      )}
                      <h2 className="font-heading text-lg font-bold text-slate-900 dark:text-white mb-2 leading-snug">{p.title}</h2>
                      <p className="text-sm text-slate-500 dark:text-slate-400 font-body leading-relaxed line-clamp-3">{p.excerpt}</p>
                      <span className="inline-flex items-center gap-1.5 text-sm font-body font-semibold text-brand-purple dark:text-brand-purple-light mt-4">
                        Read article <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                      </span>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>

      <footer className="bg-slate-50 dark:bg-[#0a0b10] border-t border-slate-100 dark:border-slate-800 py-12 px-6 mt-12">
        <div className="max-w-7xl mx-auto text-center">
          <p className="font-heading text-lg font-bold text-brand-purple dark:text-brand-purple-light">Tutors Melbourne</p>
          <p className="text-sm text-brand-gold font-body font-semibold">Learning Perfected</p>
        </div>
      </footer>
    </div>
  );
}
